import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type ReactElement } from 'react';
import type { BitrixOpenlines } from '@chatapp-inbox/shared';
import { applyBitrixOpenlines, setBitrixHandoff, setBitrixOpenlines } from '../../api';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import {
  bitrixErrorMessage,
  HANDOFF_OFF_TEXT,
  HANDOFF_ON_TEXT,
  OPENLINES_APPLIED_TEXT,
  OPENLINES_OFF_TEXT,
  OPENLINES_ON_TEXT,
  openlinesProblemText,
  widgetProblemText,
} from './bitrix-text';
import { bitrixOpenlinesKey } from './keys';
import { applyOpenlines, useBitrixOpenlines } from './use-bitrix-admin';
import { Switch } from './Switch';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './BitrixSection.module.css';

/**
 * Bate-papo no card do Bitrix (Canal Aberto; docs/bitrix-openlines.md): as mensagens do WhatsApp aparecem no
 * chat do Lead e o que se responde lá volta ao cliente. Dois passos: preparar o portal (registra o conector e
 * uma linha aberta por número) e ligar. O Bitrix cria o Lead sozinho pela linha aberta.
 */
export function BitrixOpenlinesSection(): ReactElement {
  const queryClient = useQueryClient();
  const headingId = useId();
  const hintId = useId();
  const handoffHintId = useId();
  const status = useStatusMessage();
  const openlines = useBitrixOpenlines();
  const [error, setError] = useState<string | null>(null);
  const data: BitrixOpenlines | undefined = openlines.data;

  const toggle = useMutation({ mutationFn: setBitrixOpenlines });
  const apply = useMutation({ mutationFn: applyBitrixOpenlines });
  const handoff = useMutation({ mutationFn: setBitrixHandoff });
  const busy = toggle.isPending || apply.isPending || handoff.isPending;

  const flip = (): void => {
    if (!data || busy) return;
    setError(null);
    status.announce('');
    toggle.mutate(!data.enabled, {
      onSuccess: (next) => {
        applyOpenlines(queryClient, next);
        status.announce(next.enabled ? OPENLINES_ON_TEXT : OPENLINES_OFF_TEXT);
      },
      onError: (failure) =>
        setError(bitrixErrorMessage(failure, 'Não foi possível salvar. Tente de novo.')),
    });
  };

  const flipHandoff = (): void => {
    if (!data || busy) return;
    setError(null);
    status.announce('');
    handoff.mutate(!data.handoffOnAssign, {
      onSuccess: (next) => {
        applyOpenlines(queryClient, next);
        status.announce(next.handoffOnAssign ? HANDOFF_ON_TEXT : HANDOFF_OFF_TEXT);
      },
      onError: (failure) =>
        setError(bitrixErrorMessage(failure, 'Não foi possível salvar. Tente de novo.')),
    });
  };

  const prepare = (): void => {
    if (busy) return;
    setError(null);
    status.announce('');
    apply.mutate(undefined, {
      onSuccess: (next) => {
        applyOpenlines(queryClient, next);
        status.announce(OPENLINES_APPLIED_TEXT);
      },
      onError: (failure) => {
        setError(
          bitrixErrorMessage(failure, 'Não foi possível preparar o Canal Aberto. Tente de novo.'),
        );
        void queryClient.invalidateQueries({ queryKey: bitrixOpenlinesKey });
      },
    });
  };

  const problem = openlinesProblemText(data?.lastError ?? null);
  const widgetProblem = widgetProblemText(data?.widget.error ?? null);

  return (
    <section aria-labelledby={headingId} className={section.card}>
      <h3 id={headingId} className={section.cardTitle}>
        Bate-papo no card (Canal Aberto)
      </h3>
      <p id={hintId} className={forms.hint}>
        As mensagens do WhatsApp aparecem no chat do card do Bitrix, e o que um atendente responde
        lá chega ao cliente (e ao CladvApp), e vice-versa. Com isto ligado, o Bitrix cria o Lead
        sozinho pela linha aberta; o CladvApp só procura e mostra o vínculo.
      </p>
      <p className={forms.hint}>
        Antes: no aplicativo local do Bitrix, marque também as permissões{' '}
        <strong>Canais Abertos</strong> e <strong>Bot de chat</strong>, salve e clique em{' '}
        <strong>Reinstalar</strong> no cartão do aplicativo (só salvar não repete a instalação, e é
        ela que entrega as permissões novas). Depois, clique em <strong>Preparar no Bitrix</strong>.
      </p>

      {openlines.isPending && (
        <p role="status" className={forms.hint}>
          Carregando…
        </p>
      )}
      {openlines.isError && (
        <div className={styles.loadError}>
          <p role="alert" className={forms.alert}>
            {bitrixErrorMessage(openlines.error, 'Não foi possível carregar o Canal Aberto.')}
          </p>
          <Button busy={openlines.isFetching} onClick={() => void openlines.refetch()}>
            Tentar de novo
          </Button>
        </div>
      )}

      {data && (
        <>
          <p className={styles.state}>
            <span className={styles.term}>Portal:</span>{' '}
            {data.ready ? 'preparado (conector registrado)' : 'ainda não preparado'}
          </p>
          <p className={styles.state}>
            <span className={styles.term}>
              Atalhos “Modelos de mensagem” e “Gravar áudio” no chat do Bitrix:
            </span>{' '}
            {data.widget.registered ? 'registrado' : 'ainda não registrado'}
          </p>
          {widgetProblem && (
            <p role="note" className={styles.notice}>
              {widgetProblem}
              {data.widget.errorDetail ? ` (Detalhe: ${data.widget.errorDetail}.)` : ''}
            </p>
          )}
          {!data.hasApplicationToken && (
            <p role="note" className={styles.notice}>
              O Bitrix ainda não entregou o token de confirmação. No Bitrix, abra o aplicativo local
              e clique em Reinstalar no cartão do aplicativo (ele reenvia a instalação) para poder
              ligar.
            </p>
          )}
          {problem && (
            <p role="alert" className={forms.alert}>
              {problem}
              {data.lastErrorDetail ? ` (Detalhe: ${data.lastErrorDetail}.)` : ''}
            </p>
          )}

          {data.lines.length > 0 && (
            <ul aria-label="Números e linhas abertas" className={forms.hint}>
              {data.lines.map((line) => (
                <li key={line.id}>
                  {line.name}:{' '}
                  {line.active && line.bitrixLineId !== null
                    ? `linha aberta nº ${line.bitrixLineId}, ativa`
                    : 'ainda sem linha no Bitrix'}
                </li>
              ))}
            </ul>
          )}

          <div className={forms.actions}>
            <Button busy={apply.isPending} onClick={prepare}>
              {apply.isPending ? 'Preparando…' : 'Preparar no Bitrix'}
            </Button>
          </div>

          <div className={styles.switchRow}>
            <Switch
              checked={data.enabled}
              label="Levar as conversas para o chat do Bitrix"
              busy={busy}
              describedBy={hintId}
              onToggle={flip}
            />
            <span className={styles.switchLabel} aria-hidden="true">
              Levar as conversas para o chat do Bitrix: {data.enabled ? 'ligado' : 'desligado'}
            </span>
          </div>

          <p id={handoffHintId} className={forms.hint}>
            O bot, se houver, é da própria linha no Bitrix (Contact Center → Canais Abertos → a
            linha → Configurar), e é a regra da linha que manda. Com a opção abaixo{' '}
            <strong>ligada</strong>, o CladvApp só mexe numa conversa do Bitrix quando alguém{' '}
            <strong>assume de propósito</strong> (Assumir, atribuir ou transferir, ou enviar um
            modelo pelo atalho dentro do Bitrix): aí pede que o Bitrix tire o bot e passe a conversa
            a um atendente livre da linha. Abrir uma <strong>Nova conversa</strong> não conta.{' '}
            <strong>Desligada</strong>, o CladvApp não altera nada no Bitrix: só espelha as
            mensagens e mantém os chats abertos.
          </p>
          <div className={styles.switchRow}>
            <Switch
              checked={data.handoffOnAssign}
              label="Permitir que o CladvApp altere a conversa no Bitrix"
              busy={busy}
              describedBy={handoffHintId}
              onToggle={flipHandoff}
            />
            <span className={styles.switchLabel} aria-hidden="true">
              Permitir que o CladvApp altere a conversa no Bitrix:{' '}
              {data.handoffOnAssign ? 'ligado' : 'desligado'}
            </span>
          </div>

          {data.pending > 0 && (
            <p className={forms.hint}>
              Mensagens esperando para ir ao Bitrix: {data.pending}
              {data.retrying > 0 ? ` (${data.retrying} esperando para tentar de novo)` : ''}.
            </p>
          )}
        </>
      )}

      <StatusRegion message={status.message} />
      {error && (
        <p role="alert" className={forms.alert}>
          {error}
        </p>
      )}
    </section>
  );
}
